import { AssetTypeRepository, MethodType, MethodTypeRepository } from '@telespot/domain';
import { MethodTypeConfigService } from '@protocols/domain';

/**
 * Flat, UI-ready projection of a method type for the method-type editor.
 *
 * `sampleNames` is normalized from `config.defaultSamples` into plain
 * localized records (legacy `required` flags and empty/null language values
 * are dropped). `customConfig` is the raw config when it carries structure the
 * chips editor cannot represent (`MethodTypeConfigService.isValidConfig`
 * rejects it) — the editor opens its JSON editor seeded with it; otherwise it
 * is `null` and the normal chips UI loads. `acquisitionFields` mirrors the
 * persisted `assetDataFields`.
 * `assignedResources` lists the method type's direct resource children only
 * (`{ id, name }`) — grandchildren lazy-load via `RetrieveResourceChildrenQuery`.
 * `assetTypeName` is resolved through the `AssetTypeRepository` so the editor
 * can display the current selection even when it is not part of the loaded
 * options page; it stays `undefined` when the lookup fails.
 *
 * @group Protocols
 * @category Payloads
 */
export interface RetrieveMethodTypeOutput {
  id: string;
  name: string;
  assetTypeId?: string;
  assetTypeName?: string;
  sampleNames: Record<string, string>[];
  customConfig: Record<string, unknown> | null;
  acquisitionFields: { displayName: string; fieldPath: string }[];
  assignedResources: { id: string; name: string }[];
}

/**
 * Token-style error codes thrown by the retrieve-method-type query.
 *
 * Following the documented command/query error pattern: the enum value is
 * a stable namespaced identifier callers can disambiguate without parsing
 * free-form messages.
 */
export enum RetrieveMethodTypeErrorCode {
  METHOD_TYPE_NOT_FOUND = 'retrieve_method_type.method_type_not_found',
  REPOSITORY_ERROR = 'retrieve_method_type.repository_error',
}

/**
 * Thrown when retrieving a method type fails.
 *
 * `details` carries structured context (e.g. `{ methodTypeId }`) so callers
 * can render or log the failure without scraping the message.
 */
export class RetrieveMethodTypeException extends Error {
  constructor(public readonly code: RetrieveMethodTypeErrorCode, public readonly details: Record<string, string> = {}) {
    super(code.toString());
    this.name = 'RetrieveMethodTypeException';
    Object.setPrototypeOf(this, RetrieveMethodTypeException.prototype);
  }
}

/**
 * Use case to retrieve a method type projected for the method-type editor.
 */
export interface IRetrieveMethodTypeQuery {
  /**
   * Retrieve the method type and resolve its asset-type name.
   * @param methodTypeId The id of the method type to retrieve.
   * @returns The method type as flat fields plus normalized sample names,
   * acquisition fields and directly assigned resources.
   * @throws RetrieveMethodTypeException when the operation fails — a missing
   * method type maps to `METHOD_TYPE_NOT_FOUND`; a failed asset-type lookup
   * does NOT fail the query (`assetTypeName` stays `undefined`).
   */
  execute(methodTypeId: string): Promise<RetrieveMethodTypeOutput>;
}

export class RetrieveMethodTypeQuery implements IRetrieveMethodTypeQuery {
  constructor(
    private readonly methodTypeRepository: MethodTypeRepository,
    private readonly assetTypeRepository: AssetTypeRepository
  ) {}

  public async execute(methodTypeId: string): Promise<RetrieveMethodTypeOutput> {
    const methodType = await this.getMethodType(methodTypeId);

    return {
      id: methodType.id,
      name: methodType.name,
      assetTypeId: methodType.assetTypeId,
      assetTypeName: await this.resolveAssetTypeName(methodType.assetTypeId),
      sampleNames: MethodTypeConfigService.toSampleNames(methodType.config),
      customConfig: this.toCustomConfig(methodType.config),
      acquisitionFields: this.toAcquisitionFields(methodType),
      assignedResources: this.toAssignedResources(methodType),
    };
  }

  /**
   * The raw config when the chips editor cannot faithfully represent it
   * (`isValidConfig` rejects it) — the editor seeds its JSON editor with this
   * blob. `null` for a conventional or empty config, which loads the normal
   * chips UI. `config` is `any` off the entity getter (never nullish — it
   * returns `{}`), so the rejected branch is always an object in practice.
   */
  private toCustomConfig(config: unknown): Record<string, unknown> | null {
    return MethodTypeConfigService.isValidConfig(config) ? null : (config as Record<string, unknown>);
  }

  private async getMethodType(methodTypeId: string): Promise<MethodType> {
    let methodType: MethodType | undefined;
    try {
      methodType = await this.methodTypeRepository.getById(methodTypeId);
    } catch (error) {
      throw this.toException(error, methodTypeId);
    }
    if (!methodType) {
      throw new RetrieveMethodTypeException(RetrieveMethodTypeErrorCode.METHOD_TYPE_NOT_FOUND, { methodTypeId });
    }
    return methodType;
  }

  private toException(error: unknown, methodTypeId: string): RetrieveMethodTypeException {
    if (this.isNotFoundError(error)) {
      return new RetrieveMethodTypeException(RetrieveMethodTypeErrorCode.METHOD_TYPE_NOT_FOUND, { methodTypeId });
    }
    return new RetrieveMethodTypeException(RetrieveMethodTypeErrorCode.REPOSITORY_ERROR, { methodTypeId });
  }

  private isNotFoundError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    return /object not found/i.test(error.message);
  }

  /**
   * A failed lookup never fails the query — the editor can still render the
   * raw `assetTypeId` while the name simply stays `undefined`.
   */
  private async resolveAssetTypeName(assetTypeId: string | undefined): Promise<string | undefined> {
    if (!assetTypeId) return undefined;
    try {
      const assetType = await this.assetTypeRepository.getById(assetTypeId);
      return assetType?.name;
    } catch {
      return undefined;
    }
  }

  private toAcquisitionFields(methodType: MethodType): { displayName: string; fieldPath: string }[] {
    return methodType.assetDataFields
      .filter((field): field is { displayName: string; fieldPath: string } => !!field)
      .map((field) => ({ displayName: field.displayName, fieldPath: field.fieldPath }));
  }

  private toAssignedResources(methodType: MethodType): { id: string; name: string }[] {
    return methodType.resources.map((resource) => ({ id: resource.id, name: resource.name }));
  }
}
